import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useInlineEditorBehavior } from '../../hooks/useInlineEditorBehavior'

interface InlineTextEditorProps {
  /** Current value */
  value: string | number | null | undefined
  /** Field name for display/debugging purposes */
  fieldName: string
  /** Called when user saves a new value */
  onSave: (newValue: string | number | null) => Promise<void>
  /** Called when user cancels editing */
  onCancel: () => void
  /** Input type - 'text' or 'number' */
  inputType?: 'text' | 'number'
  /** Optional placeholder text */
  placeholder?: string
  /** Optional min value for number inputs */
  min?: number
  /** Optional max value for number inputs */
  max?: number
}

/**
 * Inline editor for text/number fields in market tables.
 * - Auto-saves on blur or Enter key
 * - Cancels on Escape key
 */
export default function InlineTextEditor({
  value,
  fieldName,
  onSave,
  onCancel,
  inputType = 'text',
  placeholder = '',
  min,
  max
}: InlineTextEditorProps) {
  const initialValue = value !== null && value !== undefined ? String(value) : ''
  const [inputValue, setInputValue] = useState(initialValue)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus and select input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [])

  const handleSave = useCallback(async () => {
    if (isSaving) return

    // Parse the value based on input type
    let newValue: string | number | null
    if (inputType === 'number') {
      if (inputValue.trim() === '') {
        newValue = null
      } else {
        const parsed = parseInt(inputValue, 10)
        newValue = isNaN(parsed) ? null : parsed
      }
    } else {
      newValue = inputValue.trim() === '' ? null : inputValue.trim()
    }

    // Check if value actually changed
    const oldValue = value ?? null
    const normalizedOldValue = oldValue !== null && oldValue !== undefined ?
      (inputType === 'number' ? Number(oldValue) : String(oldValue)) : null

    if (newValue === normalizedOldValue) {
      onCancel()
      return
    }

    setIsSaving(true)
    try {
      await onSave(newValue)
    } catch (error) {
      setIsSaving(false)
    }
  }, [inputValue, value, onSave, onCancel, fieldName, isSaving, inputType])

  // InlineTextEditor renders a bare input (no wrapper div), so use inputRef as the container
  const { handleKeyDown, handleBlur } = useInlineEditorBehavior({
    onSave: handleSave,
    onCancel,
    containerRef: inputRef
  })

  return (
    <input
      ref={inputRef}
      type={inputType}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      disabled={isSaving}
      placeholder={placeholder}
      min={min}
      max={max}
      className={`
        w-full px-1.5 py-0.5 text-sm text-center
        border border-green-500 rounded
        focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent
        ${isSaving ? 'bg-gray-100 text-gray-500' : 'bg-white'}
        [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none
      `}
      style={{ minWidth: '40px', maxWidth: '80px' }}
    />
  )
}

